import React, { useCallback, useEffect, useState } from 'react';
import { useApiCaller } from '../../utils/ApiUtilities';
import { useError } from '../ErrorContext';
import {
    getPromoCodesUrl,
    getPromoCodePostUrl,
    getPromoCodePutUrl,
    getPromoCodeDeleteUrl,
    getPlansUrl,
} from '../../utils/UrlUtilities';
import { constructPromoCodePayload } from '../../utils/PayloadUtils';
import { PROMO_DISCOUNT_TYPES, itemsPerPageOptions } from '../../utils/Constants';
import { isEmpty } from '../../utils/BasicUtilities';

const EMPTY_FORM = {
    id: null, code: '', description: '', partnerLabel: '', discountType: 'PERCENT',
    discountValue: '', appliesTo: [], maxRedemptions: '', perUserLimit: '1',
    validFrom: '', expiresAt: '', isActive: true,
};

// Convert a stored UTC ISO instant to the admin's LOCAL wall-clock for a
// <input type="datetime-local">. Must pair with new Date(value).toISOString() on save
// so the round-trip is stable — otherwise each edit shifts the instant by the tz offset.
function toDatetimeLocal(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

export default function PromoCodeManagement() {
    const CallApi = useApiCaller();
    const { showSuccess } = useError();

    const [codes, setCodes] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [loading, setLoading] = useState(false);

    const [pending, setPending] = useState({ search: '', partner_label: '', is_active: '' });
    const [applied, setApplied] = useState({ search: '', partner_label: '', is_active: '' });

    const [plans, setPlans] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    // Plan codes for the "applies to" selector (empty selection = all plans).
    useEffect(() => {
        (async () => {
            const res = await CallApi({ url: getPlansUrl(), method: 'GET', token: false });
            if (res.success) setPlans(res.data?.plans || []);
        })();
    }, [CallApi]);

    const fetchCodes = useCallback(async () => {
        setLoading(true);
        const params = { page, limit };
        if (!isEmpty(applied.search)) params.search = applied.search;
        if (!isEmpty(applied.partner_label)) params.partner_label = applied.partner_label;
        if (!isEmpty(applied.is_active)) params.is_active = applied.is_active;
        const res = await CallApi({ url: getPromoCodesUrl(params), method: 'GET', token: true });
        if (res.success) {
            setCodes(res.data.promo_codes || []);
            setTotal(res.data.total || 0);
        }
        setLoading(false);
    }, [page, limit, applied, CallApi]);

    useEffect(() => { fetchCodes(); }, [fetchCodes]);

    const applyFilters = () => { setApplied(pending); setPage(1); };

    const openCreate = () => { setForm(EMPTY_FORM); setShowForm(true); };
    const openEdit = (c) => {
        setForm({
            id: c.promo_code_id, code: c.code, description: c.description, partnerLabel: c.partner_label,
            discountType: c.discount_type, discountValue: String(c.discount_value),
            appliesTo: c.applies_to || [], maxRedemptions: c.max_redemptions == null ? '' : String(c.max_redemptions),
            perUserLimit: String(c.per_user_limit), validFrom: toDatetimeLocal(c.valid_from),
            expiresAt: toDatetimeLocal(c.expires_at), isActive: c.is_active,
        });
        setShowForm(true);
    };

    const toggleAppliesTo = (planCode) => {
        setForm((f) => ({
            ...f,
            appliesTo: f.appliesTo.includes(planCode)
                ? f.appliesTo.filter((p) => p !== planCode)
                : [...f.appliesTo, planCode],
        }));
    };

    const handleSave = async () => {
        if (isEmpty(form.code)) return;
        setSaving(true);
        const payload = constructPromoCodePayload({
            code: form.code, description: form.description, partnerLabel: form.partnerLabel,
            discountType: form.discountType, discountValue: form.discountValue, appliesTo: form.appliesTo,
            maxRedemptions: form.maxRedemptions, perUserLimit: form.perUserLimit,
            validFrom: form.validFrom, expiresAt: form.expiresAt, isActive: form.isActive,
        });
        const res = form.id
            ? await CallApi({ url: getPromoCodePutUrl(form.id), method: 'PUT', payload, token: true })
            : await CallApi({ url: getPromoCodePostUrl(), method: 'POST', payload, token: true });
        setSaving(false);
        if (res.success) {
            setShowForm(false);
            showSuccess(form.id ? 'Promo code updated.' : 'Promo code created.');
            fetchCodes();
        }
    };

    const handleDelete = async (c) => {
        if (!window.confirm(`Delete promo code "${c.code}"?`)) return;
        const res = await CallApi({ url: getPromoCodeDeleteUrl(c.promo_code_id), method: 'DELETE', token: true });
        if (res.success) { showSuccess('Promo code deleted.'); fetchCodes(); }
    };

    const discountLabel = (c) => (c.discount_type === 'PERCENT' ? `${c.discount_value}%` : `৳${c.discount_value}`);
    const totalPages = Math.ceil(total / limit) || 1;

    return (
        <div className="container mt-4">
            <h2 className="fw-bold mb-3" style={{ color: "#4c1d95" }}>Promo Code Management</h2>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">Promo Codes ({total})</h4>
                <button className="btn btn-primary" onClick={openCreate}>+ Add Promo Code</button>
            </div>

            {/* Filters */}
            <div className="card mb-3 kp-filter"><div className="card-body row g-2 align-items-end">
                <div className="col-6 col-md-4">
                    <label className="form-label small mb-1">Search</label>
                    <input className="form-control form-control-sm" value={pending.search} onChange={(e) => setPending({ ...pending, search: e.target.value })} placeholder="Code, description, partner" />
                </div>
                <div className="col-6 col-md-3">
                    <label className="form-label small mb-1">Partner</label>
                    <input className="form-control form-control-sm" value={pending.partner_label} onChange={(e) => setPending({ ...pending, partner_label: e.target.value })} placeholder="Coaching center" />
                </div>
                <div className="col-6 col-md-2">
                    <label className="form-label small mb-1">Active</label>
                    <select className="form-select form-select-sm" value={pending.is_active} onChange={(e) => setPending({ ...pending, is_active: e.target.value })}>
                        <option value="">All</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                    </select>
                </div>
                <div className="col-6 col-md-2">
                    <button className="btn btn-sm btn-outline-primary w-100" onClick={applyFilters}>Filter</button>
                </div>
            </div></div>

            {/* Grid */}
            <div className="card"><div className="card-body p-0 table-responsive">
                <table className="table table-hover mb-0">
                    <thead className="table-light">
                        <tr>
                            <th>Code</th><th>Discount</th><th>Applies To</th><th>Redemptions</th>
                            <th>Per User</th><th>Expires</th><th>Active</th><th>Partner</th><th className="text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={9} className="text-center text-muted py-4">Loading…</td></tr>
                        ) : codes.length === 0 ? (
                            <tr><td colSpan={9} className="text-center text-muted py-4">No promo codes found.</td></tr>
                        ) : codes.map(c => (
                            <tr key={c.promo_code_id}>
                                <td className="fw-semibold">{c.code}</td>
                                <td>{discountLabel(c)}</td>
                                <td className="small">{(c.applies_to && c.applies_to.length) ? c.applies_to.join(', ') : 'All plans'}</td>
                                <td>{c.redemptions_used}{c.max_redemptions != null ? ` / ${c.max_redemptions}` : ''}</td>
                                <td>{c.per_user_limit}</td>
                                <td className="small text-muted">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '—'}</td>
                                <td>{c.is_active ? <span className="badge bg-success">Yes</span> : <span className="badge bg-warning text-dark">No</span>}</td>
                                <td className="small text-muted">{c.partner_label || '—'}</td>
                                <td className="text-end">
                                    <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => openEdit(c)}>Edit</button>
                                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(c)}>Delete</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div></div>

            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="d-flex align-items-center gap-2">
                    <label className="small mb-0">Items per page:</label>
                    <select className="form-select form-select-sm w-auto" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}>
                        {itemsPerPageOptions.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
                    <span className="small text-muted">Page {page} of {totalPages}</span>
                    <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
                </div>
            </div>

            {/* Add/Edit modal */}
            {showForm && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 }}>
                    <div className="card" style={{ width: '90%', maxWidth: 'min(640px, 94vw)', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div className="card-header fw-bold" style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#fff' }}>
                            {form.id ? 'Edit Promo Code' : 'Add Promo Code'}
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-12 col-md-6 mb-3">
                                    <label className="form-label fw-semibold">Code</label>
                                    <input className="form-control" style={{ textTransform: 'uppercase' }} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="SUMMER20" />
                                </div>
                                <div className="col-12 col-md-6 mb-3">
                                    <label className="form-label fw-semibold">Partner label (optional)</label>
                                    <input className="form-control" value={form.partnerLabel} onChange={(e) => setForm({ ...form, partnerLabel: e.target.value })} placeholder="Coaching center" />
                                </div>
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-semibold">Description</label>
                                <input className="form-control" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                            </div>
                            <div className="row">
                                <div className="col-12 col-md-6 mb-3">
                                    <label className="form-label fw-semibold">Discount type</label>
                                    <select className="form-select" value={form.discountType} onChange={(e) => setForm({ ...form, discountType: e.target.value })}>
                                        {PROMO_DISCOUNT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                </div>
                                <div className="col-12 col-md-6 mb-3">
                                    <label className="form-label fw-semibold">Discount value</label>
                                    <input type="number" min="0" className="form-control" value={form.discountValue} onChange={(e) => setForm({ ...form, discountValue: e.target.value })} placeholder={form.discountType === 'PERCENT' ? '20' : '100'} />
                                </div>
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-semibold">Applies to (leave empty = all plans)</label>
                                <div className="d-flex flex-wrap gap-3">
                                    {plans.map(p => (
                                        <div className="form-check" key={p.code}>
                                            <input className="form-check-input" type="checkbox" id={`plan-${p.code}`} checked={form.appliesTo.includes(p.code)} onChange={() => toggleAppliesTo(p.code)} />
                                            <label className="form-check-label" htmlFor={`plan-${p.code}`}>{p.name}</label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12 col-md-6 mb-3">
                                    <label className="form-label fw-semibold">Max redemptions (blank = unlimited)</label>
                                    <input type="number" min="0" className="form-control" value={form.maxRedemptions} onChange={(e) => setForm({ ...form, maxRedemptions: e.target.value })} />
                                </div>
                                <div className="col-12 col-md-6 mb-3">
                                    <label className="form-label fw-semibold">Per-user limit</label>
                                    <input type="number" min="1" className="form-control" value={form.perUserLimit} onChange={(e) => setForm({ ...form, perUserLimit: e.target.value })} />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12 col-md-6 mb-3">
                                    <label className="form-label fw-semibold">Valid from (optional)</label>
                                    <input type="datetime-local" className="form-control" value={form.validFrom} onChange={(e) => setForm({ ...form, validFrom: e.target.value })} />
                                </div>
                                <div className="col-12 col-md-6 mb-3">
                                    <label className="form-label fw-semibold">Expires at (optional)</label>
                                    <input type="datetime-local" className="form-control" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} />
                                </div>
                            </div>
                            <div className="form-check mb-2">
                                <input className="form-check-input" type="checkbox" id="promo-active" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                                <label className="form-check-label" htmlFor="promo-active">Active</label>
                            </div>
                        </div>
                        <div className="card-footer d-flex justify-content-end gap-2">
                            <button className="btn btn-outline-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                            <button className="btn btn-primary" onClick={handleSave} disabled={saving || isEmpty(form.code)}>{saving ? 'Saving…' : 'Save'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
